import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FormGroup,
  FormInput,
  FormSelect,
  NavButtons,
} from '../common';
import {
  DEFAULT_PADDING,
  downloadResizedImages,
  extractImagePalette,
  groupWalletImageSizes,
  resizeImageFile,
  WALLET_IMAGE_SIZES,
} from '../../utils/walletImageResizer';

const SourceBadgeIcon = ({ type }) => {
  if (type === 'strip') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
        <rect x="3" y="7" width="18" height="10" rx="2" />
        <path d="M8 12h8" />
      </svg>
    );
  }

  if (type === 'logo') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
        <rect x="4" y="8" width="16" height="8" rx="2" />
        <path d="M9 12h6" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
      <rect x="5" y="5" width="14" height="14" rx="3" />
      <path d="M9 12h6M12 9v6" />
    </svg>
  );
};

const STANDARD_FILL_OPTIONS = [
  { value: 'transparent', label: 'Transparent' },
  { value: 'white', label: 'White' },
  { value: 'black', label: 'Black' },
  { value: 'custom', label: 'Custom' },
];

const RECENT_COLORS_STORAGE_KEY = 'kit:wallet-resizer-recent-colors';

const normalizeFillValue = fill => {
  if (!fill) return 'transparent';
  if (fill === 'transparent' || fill === 'white' || fill === 'black') return fill;
  return fill;
};

const getFillMode = fill => {
  if (fill === 'transparent' || fill === 'white' || fill === 'black') return fill;
  return 'custom';
};

const formatFileCount = count => {
  if (count === 0) return 'No files';
  if (count === 1) return '1 file';
  return `${count} files`;
};

const WalletResizerColorPicker = ({
  label,
  value,
  onChange,
  imagePalette = [],
}) => {
  const [recentColors, setRecentColors] = useState(() => {
    try {
      const raw = localStorage.getItem(RECENT_COLORS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const normalizedValue =
    /^#([0-9a-f]{6})$/i.test(value) ? value.toLowerCase() : '#00519e';

  const persistRecentColor = useCallback(nextColor => {
    if (!/^#([0-9a-f]{6})$/i.test(nextColor || '')) return;
    setRecentColors(currentColors => {
      const normalized = nextColor.toLowerCase();
      const nextColors = [
        normalized,
        ...currentColors.filter(color => color.toLowerCase() !== normalized),
      ].slice(0, 12);
      try {
        localStorage.setItem(RECENT_COLORS_STORAGE_KEY, JSON.stringify(nextColors));
      } catch {}
      return nextColors;
    });
  }, []);

  const handleChange = nextColor => {
    onChange(nextColor);
    persistRecentColor(nextColor);
  };

  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
      <div>
        <p className="text-sm font-semibold text-slate-900">{label}</p>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={normalizedValue}
            onChange={event => handleChange(event.target.value)}
            className="h-12 w-12 cursor-pointer rounded-lg border border-slate-300 bg-transparent p-1"
          />
          <div className="h-12 w-12 rounded-xl border border-slate-200 shadow-inner" style={{ backgroundColor: normalizedValue }} />
        </div>
        <div className="min-w-0 flex-1">
          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            Hex
          </label>
          <input
            type="text"
            value={normalizedValue}
            onChange={event => onChange(event.target.value)}
            onBlur={event => {
              if (/^#([0-9a-f]{6})$/i.test(event.target.value || '')) {
                persistRecentColor(event.target.value);
              } else {
                onChange(normalizedValue);
              }
            }}
            className="form-input"
          />
        </div>
      </div>

      {imagePalette.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            Colors from uploaded images
          </p>
          <div className="flex flex-wrap gap-2">
            {imagePalette.map(color => (
              <button
                key={`image-${color}`}
                type="button"
                title={color}
                onClick={() => handleChange(color)}
                className={`h-9 w-9 rounded-lg border transition ${
                  normalizedValue === color.toLowerCase()
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'border-slate-300 hover:border-slate-400'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>
      )}

      {recentColors.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            Recent wallet resizer colors
          </p>
          <div className="flex flex-wrap gap-2">
            {recentColors.map(color => (
              <button
                key={`recent-${color}`}
                type="button"
                title={color}
                onClick={() => onChange(color)}
                className={`h-9 w-9 rounded-lg border transition ${
                  normalizedValue === color.toLowerCase()
                    ? 'border-primary ring-2 ring-primary/30'
                    : 'border-slate-300 hover:border-slate-400'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const FillModeControl = ({ label, fillValue, onChange, imagePalette = [] }) => {
  const mode = getFillMode(fillValue);
  const customColor =
    mode === 'custom' && /^#([0-9a-f]{6})$/i.test(fillValue) ? fillValue : '#00519e';

  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
      <div>
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        <p className="mt-1 text-xs text-slate-500">
          Transparent, white, and black stay available as defaults. Choose Custom to use the configurator color palette.
        </p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {STANDARD_FILL_OPTIONS.map(option => {
          const isActive = mode === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() =>
                onChange(option.value === 'custom' ? customColor : option.value)
              }
              className={`rounded-xl border px-4 py-3 text-sm font-medium transition ${
                isActive
                  ? 'border-primary/40 bg-primary/10 text-primary'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      {mode === 'custom' && (
        <WalletResizerColorPicker
          label="Custom color"
          value={customColor}
          onChange={onChange}
          imagePalette={imagePalette}
        />
      )}
    </div>
  );
};

const UploadCollection = ({
  title,
  description,
  emptyHint,
  files,
  sourceType,
  onFilesAdded,
  onFileRemoved,
}) => {
  const handleFileSelection = event => {
    const nextFiles = Array.from(event.target.files || []).filter(file =>
      file.type.startsWith('image/')
    );
    if (nextFiles.length) {
      onFilesAdded(nextFiles);
    }
    event.target.value = '';
  };

  const handleDrop = event => {
    event.preventDefault();
    const nextFiles = Array.from(event.dataTransfer.files || []).filter(file =>
      file.type.startsWith('image/')
    );
    if (nextFiles.length) {
      onFilesAdded(nextFiles);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h4 className="text-base font-semibold text-slate-900">{title}</h4>
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        </div>
        <span className="pill-tag whitespace-nowrap">{formatFileCount(files.length)}</span>
      </div>

      <label
        className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white px-4 py-7 text-center transition hover:border-primary/50 hover:bg-primary/5"
        onDragOver={event => event.preventDefault()}
        onDrop={handleDrop}
      >
        <span className="mb-3 rounded-full bg-slate-100 p-3 text-slate-600">
          <SourceBadgeIcon type={sourceType} />
        </span>
        <span className="text-sm font-semibold text-slate-800">
          Drag images here or click to browse
        </span>
        <span className="mt-1 text-xs text-slate-500">{emptyHint}</span>
        <input
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileSelection}
        />
      </label>

      {files.length > 0 && (
        <div className="mt-4 space-y-3">
          {files.map((file, index) => (
            <UploadedFileRow
              key={`${file.name}-${file.lastModified}-${index}`}
              file={file}
              onRemove={() => onFileRemoved(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const UploadedFileRow = ({ file, onRemove }) => {
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-slate-900">{file.name}</p>
          <p className="text-xs text-slate-500">
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </p>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
        >
          Remove
        </button>
      </div>
      <div className="bg-slate-50 p-3">
        <div className="flex h-32 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white">
          {previewUrl ? (
            <img src={previewUrl} alt={file.name} className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="text-xs text-slate-400">Preview unavailable</span>
          )}
        </div>
      </div>
    </div>
  );
};

const SizeCategoryCard = ({
  category,
  sizes,
  selectedSizes,
  onToggleSize,
  onToggleCategory,
}) => {
  const selectedCount = sizes.filter(size => selectedSizes.has(size.name)).length;
  const allSelected = selectedCount === sizes.length;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="pill-tag">{selectedCount}/{sizes.length}</span>
          <h4 className="text-base font-semibold text-slate-900">{category}</h4>
        </div>
        <button
          type="button"
          onClick={() => onToggleCategory(category)}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {sizes.map(size => {
          const isSelected = selectedSizes.has(size.name);
          return (
            <button
              key={size.name}
              type="button"
              onClick={() => onToggleSize(size.name)}
              className={`rounded-xl border px-4 py-4 text-left transition ${
                isSelected
                  ? 'border-primary/40 bg-primary/10 text-primary shadow-sm'
                  : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white'
              }`}
            >
              <p className="text-sm font-semibold">{size.name}</p>
              <p className={`mt-1 text-xs ${isSelected ? 'text-primary/80' : 'text-slate-500'}`}>
                {size.width} x {size.height}px
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const getImageTypeLabel = imageType => {
  if (imageType === 'strip') return 'Strip';
  if (imageType === 'logo') return 'Logo / Misc';
  return 'Icon';
};

const clonePadding = padding => ({
  top: Number(padding?.top || 0),
  right: Number(padding?.right || 0),
  bottom: Number(padding?.bottom || 0),
  left: Number(padding?.left || 0),
  color: normalizeFillValue(padding?.color),
});

const WalletImageResizerSection = ({
  settings,
  onNavigate,
  showNotification,
  regmailEnabled,
}) => {
  const [iconImages, setIconImages] = useState([]);
  const [miscImages, setMiscImages] = useState([]);
  const [stripImages, setStripImages] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState(() => new Set());
  const [sourceSelections, setSourceSelections] = useState({});
  const [showRarelyUsed, setShowRarelyUsed] = useState(false);
  const [backgroundFill, setBackgroundFill] = useState('transparent');
  const [paddingEnabled, setPaddingEnabled] = useState(false);
  const [padding, setPadding] = useState(DEFAULT_PADDING);
  const [fileBaseName, setFileBaseName] = useState('wallet-image');
  const [filePattern, setFilePattern] = useState('{basename}_{size}');
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewSizeName, setPreviewSizeName] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [imagePalette, setImagePalette] = useState([]);

  const notify = (message, type = 'success') => {
    if (typeof showNotification === 'function') {
      showNotification(message, type);
      return;
    }
    if (type === 'error') {
      console.error(message);
      return;
    }
    console.log(message);
  };

  const groupedSizes = useMemo(
    () => groupWalletImageSizes(showRarelyUsed),
    [showRarelyUsed]
  );

  const sizeLookup = useMemo(() => {
    const lookup = new Map();
    WALLET_IMAGE_SIZES.forEach(size => lookup.set(size.name, size));
    return lookup;
  }, []);

  const sortedSelectedSizeNames = useMemo(() => {
    const selected = Array.from(selectedSizes);
    return WALLET_IMAGE_SIZES.filter(size => selected.includes(size.name)).map(
      size => size.name
    );
  }, [selectedSizes]);

  const sourceOptions = useMemo(() => {
    const options = [];

    iconImages.forEach((file, index) => {
      options.push({
        id: `icon-${index}`,
        group: 'Icons',
        label: file.name,
        file,
      });
    });

    miscImages.forEach((file, index) => {
      options.push({
        id: `misc-${index}`,
        group: 'Logo / Misc',
        label: file.name,
        file,
      });
    });

    stripImages.forEach((file, index) => {
      options.push({
        id: `strip-${index}`,
        group: 'Strips',
        label: file.name,
        file,
      });
    });

    return options;
  }, [iconImages, miscImages, stripImages]);

  const sourceOptionsById = useMemo(() => {
    const lookup = new Map();
    sourceOptions.forEach(option => lookup.set(option.id, option));
    return lookup;
  }, [sourceOptions]);

  useEffect(() => {
    let cancelled = false;

    const buildImagePalette = async () => {
      const files = sourceOptions.map(option => option.file);
      if (!files.length) {
        setImagePalette([]);
        return;
      }

      const collectedColors = [];
      for (const file of files.slice(0, 8)) {
        try {
          const colors = await extractImagePalette(file, 6);
          colors.forEach(color => {
            if (!collectedColors.includes(color)) {
              collectedColors.push(color);
            }
          });
          if (collectedColors.length >= 18) break;
        } catch {}
      }

      if (!cancelled) {
        setImagePalette(collectedColors.slice(0, 18));
      }
    };

    buildImagePalette();

    return () => {
      cancelled = true;
    };
  }, [sourceOptions]);

  const getDefaultSourceId = useCallback(sizeName => {
    const size = sizeLookup.get(sizeName);
    if (!size) return '';

    if (size.imageType === 'strip' && stripImages.length > 0) return 'strip-0';
    if (size.imageType === 'logo' && miscImages.length > 0) return 'misc-0';
    if (iconImages.length > 0) return 'icon-0';
    if (miscImages.length > 0) return 'misc-0';
    if (stripImages.length > 0) return 'strip-0';

    return '';
  }, [iconImages.length, miscImages.length, sizeLookup, stripImages.length]);

  const resolveSourceFile = useCallback(sizeName => {
    const sourceId = sourceSelections[sizeName];
    return sourceOptionsById.get(sourceId)?.file || null;
  }, [sourceOptionsById, sourceSelections]);

  useEffect(() => {
    setSourceSelections(currentSelections => {
      let didChange = false;
      const nextSelections = {};

      Array.from(selectedSizes).forEach(sizeName => {
        const currentSourceId = currentSelections[sizeName];
        if (currentSourceId && sourceOptionsById.has(currentSourceId)) {
          nextSelections[sizeName] = currentSourceId;
          return;
        }

        const defaultSourceId = getDefaultSourceId(sizeName);
        if (defaultSourceId) {
          nextSelections[sizeName] = defaultSourceId;
        }
        if (defaultSourceId !== currentSourceId) {
          didChange = true;
        }
      });

      if (!didChange && Object.keys(currentSelections).length === Object.keys(nextSelections).length) {
        return currentSelections;
      }

      return nextSelections;
    });
  }, [getDefaultSourceId, selectedSizes, sourceOptionsById]);

  useEffect(() => {
    if (!sortedSelectedSizeNames.length) {
      setPreviewSizeName('');
      return;
    }

    if (!sortedSelectedSizeNames.includes(previewSizeName)) {
      setPreviewSizeName(sortedSelectedSizeNames[0]);
    }
  }, [previewSizeName, sortedSelectedSizeNames]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';

    const buildPreview = async () => {
      if (!previewSizeName) {
        setPreviewUrl('');
        return;
      }

      const config = sizeLookup.get(previewSizeName);
      const sourceFile = resolveSourceFile(previewSizeName);

      if (!config || !sourceFile) {
        setPreviewUrl('');
        return;
      }

      try {
        const blob = await resizeImageFile({
          file: sourceFile,
          width: config.width,
          height: config.height,
          backgroundFill,
          padding: paddingEnabled ? padding : DEFAULT_PADDING,
        });

        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      } catch (error) {
        if (!cancelled) {
          setPreviewUrl('');
        }
      }
    };

    buildPreview();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [
    padding,
    paddingEnabled,
    previewSizeName,
    resolveSourceFile,
    sizeLookup,
    backgroundFill,
  ]);

  const addFiles = (collectionType, files) => {
    if (!files.length) return;

    if (collectionType === 'icon') {
      setIconImages(current => current.concat(files));
    } else if (collectionType === 'misc') {
      setMiscImages(current => current.concat(files));
    } else {
      setStripImages(current => current.concat(files));
    }

    notify(`${files.length} image${files.length > 1 ? 's' : ''} added to ${getImageTypeLabel(collectionType)} sources.`);
  };

  const removeFile = (collectionType, index) => {
    if (collectionType === 'icon') {
      setIconImages(current => current.filter((_, currentIndex) => currentIndex !== index));
    } else if (collectionType === 'misc') {
      setMiscImages(current => current.filter((_, currentIndex) => currentIndex !== index));
    } else {
      setStripImages(current => current.filter((_, currentIndex) => currentIndex !== index));
    }
  };

  const handleToggleSize = sizeName => {
    setSelectedSizes(currentSelected => {
      const nextSelected = new Set(currentSelected);
      if (nextSelected.has(sizeName)) {
        nextSelected.delete(sizeName);
      } else {
        nextSelected.add(sizeName);
      }
      return nextSelected;
    });

    setSourceSelections(currentSelections => {
      const nextSelections = { ...currentSelections };
      if (selectedSizes.has(sizeName)) {
        delete nextSelections[sizeName];
      } else {
        const defaultSourceId = getDefaultSourceId(sizeName);
        if (defaultSourceId) {
          nextSelections[sizeName] = defaultSourceId;
        }
      }
      return nextSelections;
    });
  };

  const handleToggleCategory = category => {
    const sizesInCategory = groupedSizes.find(group => group.category === category)?.sizes || [];
    const allSelected = sizesInCategory.every(size => selectedSizes.has(size.name));

    setSelectedSizes(currentSelected => {
      const nextSelected = new Set(currentSelected);
      sizesInCategory.forEach(size => {
        if (allSelected) {
          nextSelected.delete(size.name);
        } else {
          nextSelected.add(size.name);
        }
      });
      return nextSelected;
    });

    setSourceSelections(currentSelections => {
      const nextSelections = { ...currentSelections };
      sizesInCategory.forEach(size => {
        if (allSelected) {
          delete nextSelections[size.name];
          return;
        }
        if (!nextSelections[size.name]) {
          const defaultSourceId = getDefaultSourceId(size.name);
          if (defaultSourceId) {
            nextSelections[size.name] = defaultSourceId;
          }
        }
      });
      return nextSelections;
    });
  };

  const handleSourceSelection = (sizeName, sourceId) => {
    setSourceSelections(currentSelections => ({
      ...currentSelections,
      [sizeName]: sourceId,
    }));
  };

  const handlePaddingChange = (field, nextValue) => {
    setPadding(currentPadding => ({
      ...currentPadding,
      [field]:
        field === 'color'
          ? nextValue
          : Math.max(0, Number.parseInt(nextValue, 10) || 0),
    }));
  };

  const handleResetTool = () => {
    setIconImages([]);
    setMiscImages([]);
    setStripImages([]);
    setSelectedSizes(new Set());
    setSourceSelections({});
    setShowRarelyUsed(false);
    setBackgroundFill('transparent');
    setPaddingEnabled(false);
    setPadding(clonePadding(DEFAULT_PADDING));
    setFileBaseName('wallet-image');
    setFilePattern('{basename}_{size}');
    setPreviewSizeName('');
    setPreviewUrl('');
    notify('Wallet image resizer reset to its default state.');
  };

  const handleDownload = async () => {
    if (!sourceOptions.length) {
      notify('Upload at least one source image before creating wallet assets.', 'error');
      return;
    }

    if (!sortedSelectedSizeNames.length) {
      notify('Select at least one output size before downloading.', 'error');
      return;
    }

    const missingSelections = sortedSelectedSizeNames.filter(
      sizeName => !resolveSourceFile(sizeName)
    );

    if (missingSelections.length) {
      notify(
        `Choose a source image for: ${missingSelections.join(', ')}`,
        'error'
      );
      return;
    }

    setIsProcessing(true);

    try {
      const outputImages = [];

      for (const sizeName of sortedSelectedSizeNames) {
        const config = sizeLookup.get(sizeName);
        const sourceFile = resolveSourceFile(sizeName);

        if (!config || !sourceFile) continue;

        const blob = await resizeImageFile({
          file: sourceFile,
          width: config.width,
          height: config.height,
          backgroundFill,
          padding: paddingEnabled ? padding : DEFAULT_PADDING,
        });

        outputImages.push({ blob, config });
      }

      await downloadResizedImages({
        images: outputImages,
        baseName: fileBaseName || 'wallet-image',
        pattern: filePattern || '{basename}_{size}',
      });

      notify(`${outputImages.length} PNG file${outputImages.length > 1 ? 's' : ''} downloaded.`);
    } catch (error) {
      notify(error?.message || 'Failed to generate wallet images.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const prevSectionKey = () => {
    if (settings?.includeWalletButtons) return 'wallet';
    if (settings?.includeCalendarButton) return 'calendarButton';
    if (settings?.showCongressAppLinks) return 'congressAppLinks';
    return 'content';
  };

  const nextSectionKey = regmailEnabled ? 'regmailCustomization' : 'images';
  const previewConfig = previewSizeName ? sizeLookup.get(previewSizeName) : null;

  return (
    <>
      <FormGroup title="1. Upload Source Images">
        <div className="grid gap-5 xl:grid-cols-3">
          <UploadCollection
            title="Icon sources"
            description="Use for square Apple Wallet icons, Google Wallet logos, and thumbnails."
            emptyHint="PNG, JPG, GIF, or WebP"
            files={iconImages}
            sourceType="icon"
            onFilesAdded={files => addFiles('icon', files)}
            onFileRemoved={index => removeFile('icon', index)}
          />
          <UploadCollection
            title="Logo / misc sources"
            description="Use for long logo artwork that should not be treated like a strip banner."
            emptyHint="Best for 160x50, 320x100, and 480x150 outputs"
            files={miscImages}
            sourceType="logo"
            onFilesAdded={files => addFiles('misc', files)}
            onFileRemoved={index => removeFile('misc', index)}
          />
          <UploadCollection
            title="Strip / hero sources"
            description="Use for strip banners, hero images, and wide background artwork."
            emptyHint="Ideal for Apple strip images and Google Wallet hero art"
            files={stripImages}
            sourceType="strip"
            onFilesAdded={files => addFiles('strip', files)}
            onFileRemoved={index => removeFile('strip', index)}
          />
        </div>
      </FormGroup>

      <FormGroup title="2. Choose Output Sizes">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-900">Rarely used wallet sizes</p>
              <p className="mt-1 text-xs text-slate-500">
                Reveal older or less common logo, background, and thumbnail sizes.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowRarelyUsed(current => !current)}
              className={`rounded-xl border px-4 py-2 text-sm font-medium transition ${
                showRarelyUsed
                  ? 'border-primary/40 bg-primary/10 text-primary'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
              }`}
            >
              {showRarelyUsed ? 'Hide rarely used sizes' : 'Show rarely used sizes'}
            </button>
          </div>

          <div className="grid gap-4">
            {groupedSizes.map(group => (
              <SizeCategoryCard
                key={group.category}
                category={group.category}
                sizes={group.sizes}
                selectedSizes={selectedSizes}
                onToggleSize={handleToggleSize}
                onToggleCategory={handleToggleCategory}
              />
            ))}
          </div>
        </div>
      </FormGroup>

      <FormGroup title="3. Assign a Source to Each Selected Size">
        {!sortedSelectedSizeNames.length ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500">
            Select one or more output sizes to map them to a source image.
          </div>
        ) : (
          <div className="space-y-3">
            {sortedSelectedSizeNames.map(sizeName => {
              const config = sizeLookup.get(sizeName);
              const currentSourceId = sourceSelections[sizeName] || '';
              const matchingOptions = sourceOptions.filter(option => {
                if (config?.imageType === 'strip') return option.id.startsWith('strip-');
                if (config?.imageType === 'logo') return option.id.startsWith('misc-');
                return option.id.startsWith('icon-');
              });
              const fallbackOptions = matchingOptions.length ? matchingOptions : sourceOptions;

              return (
                <div
                  key={sizeName}
                  className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-4 lg:grid-cols-[minmax(0,1fr)_minmax(240px,360px)] lg:items-center"
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-slate-900">{sizeName}</p>
                      <span className="pill-tag">
                        {config.width} x {config.height}px
                      </span>
                      <span className="pill-tag">{getImageTypeLabel(config.imageType)} source</span>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      {matchingOptions.length
                        ? 'Choose the source artwork that should be resized for this output.'
                        : 'No matching source type uploaded yet. Using any available image source as a fallback.'}
                    </p>
                  </div>
                  <FormSelect
                    label="Source image"
                    value={currentSourceId}
                    onChange={event => handleSourceSelection(sizeName, event.target.value)}
                  >
                    <option value="">Select a source image</option>
                    {fallbackOptions.map(option => (
                      <option key={option.id} value={option.id}>
                        {option.group}: {option.label}
                      </option>
                    ))}
                  </FormSelect>
                </div>
              );
            })}
          </div>
        )}
      </FormGroup>

      <FormGroup title="4. Export Options">
        <div className="grid gap-5 xl:grid-cols-2">
          <div className="space-y-4">
            <FillModeControl
              label="Background fill"
              fillValue={backgroundFill}
              onChange={setBackgroundFill}
              imagePalette={imagePalette}
            />

            <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Extra padding</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Add outer space around the target dimensions before export.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPaddingEnabled(current => !current)}
                  className={`rounded-xl border px-4 py-2 text-sm font-medium transition ${
                    paddingEnabled
                      ? 'border-primary/40 bg-primary/10 text-primary'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                  }`}
                >
                  {paddingEnabled ? 'Disable padding' : 'Enable padding'}
                </button>
              </div>

              {paddingEnabled && (
                <>
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Quick presets
                    </p>
                    <div className="grid gap-2 sm:grid-cols-3 xl:grid-cols-5">
                      {[10, 20, 30, 40, 50].map(value => (
                        <button
                          key={value}
                          type="button"
                          onClick={() =>
                            setPadding({
                              top: value,
                              right: value,
                              bottom: value,
                              left: value,
                              color: padding.color,
                            })
                          }
                          className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
                        >
                          Add {value}px
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormInput
                      label="Padding top"
                      type="number"
                      value={padding.top}
                      onChange={event => handlePaddingChange('top', event.target.value)}
                    />
                    <FormInput
                      label="Padding right"
                      type="number"
                      value={padding.right}
                      onChange={event => handlePaddingChange('right', event.target.value)}
                    />
                    <FormInput
                      label="Padding bottom"
                      type="number"
                      value={padding.bottom}
                      onChange={event => handlePaddingChange('bottom', event.target.value)}
                    />
                    <FormInput
                      label="Padding left"
                      type="number"
                      value={padding.left}
                      onChange={event => handlePaddingChange('left', event.target.value)}
                    />
                  </div>

                  <FillModeControl
                    label="Padding fill"
                    fillValue={padding.color}
                    onChange={value =>
                      setPadding(currentPadding => ({
                        ...currentPadding,
                        color: normalizeFillValue(value),
                      }))
                    }
                    imagePalette={imagePalette}
                  />
                </>
              )}
            </div>
          </div>

          <div className="space-y-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
            <FormInput
              label="Base file name"
              value={fileBaseName}
              onChange={event => setFileBaseName(event.target.value)}
              placeholder="wallet-image"
              helpText="Used for every export in this batch."
            />
            <FormInput
              label="File pattern"
              value={filePattern}
              onChange={event => setFilePattern(event.target.value)}
              placeholder="{basename}_{size}"
              helpText="Available placeholders: {basename}, {size}, {width}, {height}"
            />
            <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-4 text-xs text-slate-500">
              Example output: {(filePattern || '{basename}_{size}')
                .replace(/\{basename\}/g, fileBaseName || 'wallet-image')
                .replace(/\{size\}/g, 'Icon_87x87')
                .replace(/\{width\}/g, '87')
                .replace(/\{height\}/g, '87')}
              .png
            </div>
          </div>
        </div>
      </FormGroup>

      <FormGroup title="5. Preview">
        <div className="grid gap-5 xl:grid-cols-[minmax(240px,320px)_minmax(0,1fr)]">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
            <FormSelect
              label="Preview size"
              value={previewSizeName}
              onChange={event => setPreviewSizeName(event.target.value)}
            >
              <option value="">Select a size to preview</option>
              {sortedSelectedSizeNames.map(sizeName => (
                <option key={sizeName} value={sizeName}>
                  {sizeName}
                </option>
              ))}
            </FormSelect>

            {previewConfig && (
              <div className="mt-4 space-y-2 text-xs text-slate-500">
                <p>
                  Target canvas: {previewConfig.width} x {previewConfig.height}px
                </p>
                <p>
                  Padding: {paddingEnabled
                    ? `${padding.top}/${padding.right}/${padding.bottom}/${padding.left}px`
                    : 'disabled'}
                </p>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
            <div className="flex min-h-[260px] items-center justify-center rounded-2xl border border-slate-200 bg-white p-6">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt={previewSizeName || 'Wallet preview'}
                  className="max-h-[420px] max-w-full object-contain"
                />
              ) : (
                <div className="max-w-sm text-center text-sm text-slate-500">
                  Upload a source image, choose an output size, and assign a source to render the preview.
                </div>
              )}
            </div>
          </div>
        </div>
      </FormGroup>

      <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">Download resized assets</p>
            <p className="mt-1 text-xs text-slate-500">
              Every selected size is exported as its own PNG download from the current browser session.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={handleResetTool}
              className="nav-button-secondary"
            >
              Reset Tool
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={isProcessing}
              className="nav-button-primary"
            >
              {isProcessing ? 'Generating PNGs...' : 'Download PNG Assets'}
            </button>
          </div>
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Images are not saved in this tool. Download them right away, because uploads and results are lost after refresh.
        </p>
      </div>

      <NavButtons
        onPrev={() => onNavigate(prevSectionKey())}
        onNext={() => onNavigate(nextSectionKey)}
        prevLabel="Back"
        nextLabel="Continue"
      />
    </>
  );
};

export default WalletImageResizerSection;
